import type {
  ListDefectsParams,
  ListEquipmentParams,
  ListInspectionsParams,
  ListScopeOfWorksParams,
} from "./model";
import {
  getGetEquipmentByIdQueryKey,
  getGetEquipmentSummaryQueryKey,
  getListEquipmentForDocumentQueryKey,
  getListEquipmentInfiniteQueryKey,
  getListLinkPickerEquipmentQueryKey,
  getListEquipmentQueryKey,
} from "./client-equipment";
import {
  getGetDocumentDetailQueryKey,
  getListDocumentLibraryQueryKey,
  getListDocumentTypesQueryKey,
  getListLinkReviewQueueQueryKey,
} from "./client-document";
import {
  getGetAuditTimelineInfiniteQueryKey,
  getGetAuditTimelineQueryKey,
} from "./client-audit";
import {
  getGetDefectByIdQueryKey,
  getGetDefectSummaryQueryKey,
  getListDefectsInfiniteQueryKey,
  getListDefectsQueryKey,
} from "./client-defect";
import {
  getGetHACFormQueryKey,
  getGetInspectionQueryKey,
  getGetInspectionSummaryQueryKey,
  getGetScopeOfWorkQueryKey,
  getListInspectionsInfiniteQueryKey,
  getListInspectionsQueryKey,
  getListScopeOfWorksQueryKey,
} from "./client-inspection";

// ============================================================================
// Equipment Service Keys
// ============================================================================

export const equipmentKeys = {
  lists: () => [
    getListEquipmentInfiniteQueryKey(),
    getListEquipmentQueryKey(),
  ] as const,
  list: (params?: ListEquipmentParams) =>
    [
      getListEquipmentInfiniteQueryKey(params),
      getListEquipmentQueryKey(params),
    ] as const,
  detail: (id: string) => [getGetEquipmentByIdQueryKey(id)] as const,
  // The param-less key is a prefix of every per-site variant
  // (["/api/equipment/summary"] is a prefix of ["/api/equipment/summary", { siteId }]),
  // and invalidateQueries uses prefix matching by default, so this covers all sites.
  summary: () => [getGetEquipmentSummaryQueryKey()] as const,
};

// ============================================================================
// Inspection / Scope-of-Work Keys
// ============================================================================

export const scopeOfWorkKeys = {
  lists: () => [getListScopeOfWorksQueryKey()] as const,
  list: (params?: ListScopeOfWorksParams) =>
    [getListScopeOfWorksQueryKey(params)] as const,
  detail: (id: string) => [getGetScopeOfWorkQueryKey(id)] as const,
};

export const inspectionKeys = {
  lists: () => [
    getListInspectionsInfiniteQueryKey(),
    getListInspectionsQueryKey(),
  ] as const,
  list: (params?: ListInspectionsParams) =>
    [
      getListInspectionsInfiniteQueryKey(params),
      getListInspectionsQueryKey(params),
    ] as const,
  detail: (id: string) => [getGetInspectionQueryKey(id)] as const,
  summary: () => [getGetInspectionSummaryQueryKey()] as const,
  hacForm: (id: string) => [getGetHACFormQueryKey(id)] as const,
};

// ============================================================================
// Audit Service Keys
// ============================================================================

/**
 * Timeline keys are aggregate-generic on purpose: audit-svc serves every
 * domain from one endpoint shape, so a caller names the aggregate rather than
 * getting its own key module.
 *
 * Deliberately omits the params argument. TanStack matches query keys by
 * prefix, so a key without `{limit}` invalidates every page size a caller
 * happens to use.
 */
export const auditKeys = {
  timeline: (aggregateType: string, aggregateId: string) =>
    [
      getGetAuditTimelineInfiniteQueryKey(aggregateType, aggregateId),
      getGetAuditTimelineQueryKey(aggregateType, aggregateId),
    ] as const,
};

// ============================================================================
// Defect Service Keys
// ============================================================================

export const defectKeys = {
  lists: () => [
    getListDefectsInfiniteQueryKey(),
    getListDefectsQueryKey(),
  ] as const,
  list: (params?: ListDefectsParams) =>
    [
      getListDefectsInfiniteQueryKey(params),
      getListDefectsQueryKey(params),
    ] as const,
  detail: (id: string) => [getGetDefectByIdQueryKey(id)] as const,
  audit: (id: string) => auditKeys.timeline("defect", id),
  summary: () => [getGetDefectSummaryQueryKey()] as const,
};

// ============================================================================
// Document Service Keys
// ============================================================================

export const documentKeys = {
  all: ["documents"] as const,
  download: (id: string) => [...documentKeys.all, "download", id] as const,
  detail: (id: string) => [getGetDocumentDetailQueryKey(id)] as const,
  // Param-less keys are prefixes of every per-site/group/search variant, and
  // invalidateQueries prefix-matches by default — the same trick equipmentKeys
  // uses for summary().
  library: () => [getListDocumentLibraryQueryKey()] as const,
  linkQueue: () => [getListLinkReviewQueueQueryKey()] as const,
  // The per-type counts drive the sidebar's group badges and the library's
  // summary cards, so a type change has to reach them or the sidebar lies
  // until the next reload.
  types: () => [getListDocumentTypesQueryKey()] as const,
  // Param-less for the same prefix-matching reason, which here also sweeps the
  // search and paging variants of the coverage table. It covers every
  // document's coverage rather than one — a wider net than needed, but only
  // one document's page is ever on screen.
  linkedEquipment: () => [getListEquipmentForDocumentQueryKey()] as const,
  linkPicker: () => [getListLinkPickerEquipmentQueryKey()] as const,
};

// ============================================================================
// Organisation Service Keys
// ============================================================================

export const siteMappingKeys = {
  all: ["site-mappings"] as const,
  list: (params?: Record<string, unknown>) =>
    [...siteMappingKeys.all, ...(params ? [params] : [])] as const,
};

// ============================================================================
// Invalidation Map
// ============================================================================
// Maps mutation names → query keys to invalidate after success.
// Each entry returns an array of query key tuples.

export const invalidationMap = {
  // Equipment
  createEquipment: (_params?: { siteId?: string }) => [
    ...equipmentKeys.lists(),
    ...equipmentKeys.summary(),
  ],
  patchEquipment: (id: string, _params?: { siteId?: string }) => [
    ...equipmentKeys.detail(id),
    ...equipmentKeys.lists(),
    ...equipmentKeys.summary(),
  ],
  deleteEquipment: () => [...equipmentKeys.lists(), ...equipmentKeys.summary()],
  batchDeleteEquipment: () => [...equipmentKeys.lists(), ...equipmentKeys.summary()],
  // A site purge removes the equipment, and equipment-svc cascades that into
  // the inspections and defects hanging off those rows. Every one of those
  // caches is stale afterwards, so all three domains are swept here rather
  // than waiting for the individual SSE events to arrive.
  purgeSiteEquipment: () => [
    ...equipmentKeys.lists(),
    ...equipmentKeys.summary(),
    ...inspectionKeys.lists(),
    ...inspectionKeys.summary(),
    ...scopeOfWorkKeys.lists(),
    ...defectKeys.lists(),
    ...defectKeys.summary(),
  ],
  // Clearing a site's inspections leaves equipment and defects in place, but
  // the equipment register shows each row's last inspection and the scope of
  // work lists count their inspections, so those caches go stale too.
  purgeSiteInspections: () => [
    ...inspectionKeys.lists(),
    ...inspectionKeys.summary(),
    ...scopeOfWorkKeys.lists(),
    ...equipmentKeys.lists(),
  ],
  // Clearing a site's defects leaves equipment and inspections in place. The
  // equipment register carries a defect projection, so its lists are swept.
  purgeSiteDefects: () => [
    ...defectKeys.lists(),
    ...defectKeys.summary(),
    ...equipmentKeys.lists(),
  ],

  // Scope of Work
  createScopeOfWork: () => [...scopeOfWorkKeys.lists()],
  updateScopeOfWork: (id: string) => [
    ...scopeOfWorkKeys.detail(id),
    ...scopeOfWorkKeys.lists(),
  ],
  deleteScopeOfWork: () => [
    ...scopeOfWorkKeys.lists(),
    ...inspectionKeys.lists(),
    ...inspectionKeys.summary(),
    ...equipmentKeys.lists(),
  ],
  addEquipmentToScopeOfWork: (id?: string) => [
    ...(id ? scopeOfWorkKeys.detail(id) : []),
    ...scopeOfWorkKeys.lists(),
    ...equipmentKeys.lists(),
  ],
  removeEquipmentFromScopeOfWork: (id: string) => [
    ...scopeOfWorkKeys.detail(id),
    ...scopeOfWorkKeys.lists(),
  ],
  scheduleScopeOfWork: (id: string) => [
    ...scopeOfWorkKeys.detail(id),
    ...scopeOfWorkKeys.lists(),
    ...inspectionKeys.lists(),
    ...inspectionKeys.summary(),
  ],

  // Document
  //
  // Explicit rather than SSE-driven. use-sse.ts handles document.uploaded only
  // — link and unlink stopped flowing through document.* for the client, and a
  // rename has no browser-facing event at all. Equipment detail can lean on SSE
  // because equipment.updated fires for the record on screen; this can't.
  saveDocumentEdit: (id: string, typeChanged: boolean) => [
    ...documentKeys.detail(id),
    ...documentKeys.library(),
    // The per-type counts drive the sidebar badges and the library's summary
    // cards; without this the sidebar lies until the next reload.
    ...(typeChanged ? documentKeys.types() : []),
  ],
  documentLinksChanged: (id: string) => [
    // The header count and the coverage table.
    ...documentKeys.detail(id),
    ...documentKeys.linkedEquipment(),
    ...documentKeys.linkPicker(),
    // The library's "linked to" column.
    ...documentKeys.library(),
    // A newly-linked document leaves the review queue; a newly-unlinked one
    // rejoins it.
    ...documentKeys.linkQueue(),
    // Every equipment detail page: which tags moved isn't known here, and each
    // one embeds its own document list.
    ...equipmentKeys.lists(),
  ],

  // Defect
  createDefect: () => [...defectKeys.lists(), ...defectKeys.summary()],
  deleteDefect: () => [...defectKeys.lists(), ...defectKeys.summary()],
  updateDefect: (id: string) => [
    ...defectKeys.detail(id),
    ...defectKeys.audit(id),
    ...defectKeys.lists(),
    ...defectKeys.summary(),
  ],
  updateDefectStatus: (id: string) => [
    ...defectKeys.detail(id),
    ...defectKeys.audit(id),
    ...defectKeys.lists(),
    ...defectKeys.summary(),
  ],
  addCorrectiveAction: (id: string) => [...defectKeys.detail(id)],
  setCorrectiveActionCompleted: (defectId: string) => [
    ...defectKeys.detail(defectId),
  ],
  closeOutDefect: (id: string) => [
    ...defectKeys.detail(id),
    ...defectKeys.audit(id),
    ...defectKeys.lists(),
    ...defectKeys.summary(),
  ],

  // Inspection
  createInspection: () => [
    ...inspectionKeys.lists(),
    ...inspectionKeys.summary(),
  ],
  updateInspection: (id: string) => [
    ...inspectionKeys.detail(id),
    ...inspectionKeys.lists(),
    ...inspectionKeys.summary(),
  ],
  startInspection: (id: string) => [
    ...inspectionKeys.detail(id),
    ...inspectionKeys.lists(),
    ...inspectionKeys.summary(),
    ...scopeOfWorkKeys.lists(),
  ],
  completeInspection: (id: string) => [
    ...inspectionKeys.detail(id),
    ...inspectionKeys.lists(),
    ...inspectionKeys.summary(),
    ...scopeOfWorkKeys.lists(),
  ],
  submitHACForm: (id: string) => [
    ...inspectionKeys.hacForm(id),
    ...inspectionKeys.detail(id),
    ...defectKeys.lists(),
    ...defectKeys.summary(),
  ],

  // Scope of Work cancellation — cascades non-terminal child inspections.
  cancelScopeOfWork: (id: string) => [
    ...scopeOfWorkKeys.detail(id),
    ...scopeOfWorkKeys.lists(),
    ...inspectionKeys.lists(),
    ...inspectionKeys.summary(),
  ],

  // Single-inspection cancel — invalidates the inspection and the parent SOW
  // (the SOW may auto-complete if every remaining sibling is terminal).
  cancelInspection: (id: string, scopeOfWorkId?: string) => [
    ...inspectionKeys.detail(id),
    ...inspectionKeys.lists(),
    ...inspectionKeys.summary(),
    ...(scopeOfWorkId
      ? [
          ...scopeOfWorkKeys.detail(scopeOfWorkId),
          ...scopeOfWorkKeys.lists(),
        ]
      : []),
  ],
} as const;
